import React, { useEffect, useRef, useState } from 'react';
import {
  Button, Checkbox, Form, Input, Modal, Select, Space, Steps, Switch, Tabs, Tag, Tooltip, Upload, Typography, message,
} from 'antd';
import {
  ApiOutlined,
  AudioOutlined,
  BookOutlined,
  BranchesOutlined,
  CheckCircleOutlined,
  CodeOutlined,
  DeleteOutlined,
  EditOutlined,
  GlobalOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
  MenuOutlined,
  PictureOutlined,
  PlusOutlined,
  RobotOutlined,
  RocketOutlined,
  SearchOutlined,
  ToolOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import * as AntdIcons from '@ant-design/icons';
import * as api from '../api/client';
import type { AgentRecord, AgentCategory, AgentVisibility, AgentType, ToolRecord } from '../api/client';
import { renderAgentIcon } from './AgentsView';
import DictationOverlay from './DictationOverlay';

const { Text, Title, Paragraph } = Typography;
const { TextArea } = Input;

const isRenderableAntdIcon = (value: unknown): value is React.ComponentType<any> => {
  return Boolean(value) && (typeof value === 'function' || typeof value === 'object');
};

const PICKER_ICON_NAMES: string[] = Object.keys(AntdIcons)
  .filter((name) => name.endsWith('Outlined'))
  .filter((name) => isRenderableAntdIcon((AntdIcons as any)[name]))
  .sort();

const DEFAULT_PICKER_ICON_NAMES = [
  'RobotOutlined',
  'RocketOutlined',
  'ApiOutlined',
  'BookOutlined',
  'BranchesOutlined',
  'ToolOutlined',
  'CodeOutlined',
  'DatabaseOutlined',
  'CloudServerOutlined',
  'SecurityScanOutlined',
  'FileSearchOutlined',
  'ExperimentOutlined',
  'BulbOutlined',
  'ThunderboltOutlined',
  'GlobalOutlined',
  'TeamOutlined',
].filter((name) => PICKER_ICON_NAMES.includes(name));

const AGENT_CATEGORIES: { value: AgentCategory; label: string }[] = [
  { value: 'SOFTWARE_ENGINEERING', label: 'Software Engineering' },
  { value: 'DEVOPS', label: 'DevOps' },
  { value: 'AGILE', label: 'Agile' },
  { value: 'PROJECT_MANAGEMENT', label: 'Project Management' },
  { value: 'GOVERNMENT_CONTRACTING', label: 'Government Contracting' },
  { value: 'BUSINESS_DEVELOPMENT', label: 'Business Development' },
  { value: 'SALES', label: 'Sales' },
  { value: 'MARKETING', label: 'Marketing' },
  { value: 'RESEARCH', label: 'Research' },
  { value: 'HR', label: 'HR' },
  { value: 'FINANCE', label: 'Finance' },
  { value: 'OPERATIONS', label: 'Operations' },
  { value: 'CUSTOM', label: 'Custom' },
];

const VISIBILITY_OPTIONS: { value: AgentVisibility; label: string; description: string }[] = [
  { value: 'PRIVATE', label: 'Private', description: 'Only you can see and use this agent' },
  { value: 'ORGANIZATION', label: 'Organization', description: 'All members of your organization' },
  { value: 'PUBLIC', label: 'Public', description: 'Discoverable on the Marketplace by everyone' },
];

const AGENT_TYPES: { value: AgentType; label: string; description: string; icon: React.ReactNode }[] = [
  { value: 'KNOWLEDGE', label: 'Knowledge', description: 'Answer questions from attached knowledge', icon: <BookOutlined /> },
  { value: 'TASK', label: 'Task', description: 'Plan and execute goals with tools', icon: <RobotOutlined /> },
  { value: 'WORKFLOW', label: 'Workflow', description: 'Run a Playbook-backed orchestration', icon: <BranchesOutlined /> },
  { value: 'API', label: 'API', description: 'Expose a typed execution endpoint', icon: <ApiOutlined /> },
];

const FIELD_TYPES = [
  'String', 'Number', 'Boolean', 'Password', 'Date', 'JSON', 'File', 'Document', 'URL',
];

const OUTPUT_TYPES = [
  'String', 'Number', 'Boolean', 'JSON', 'Markdown','HTML','Document', 'Array', 'Object',
];

export interface InputField {
  name: string;
  type: string;
  description: string;
  required: boolean;
  secret: boolean;
}

export interface OutputField {
  name: string;
  type: string;
  description: string;
}

interface KBSummary {
  id: string;
  name: string;
  description?: string;
}

interface Props {
  visible: boolean;
  userId: string;
  existingAgent?: AgentRecord | null;
  onClose: () => void;
  onSaved: () => void;
}

type StepKey = 'general' | 'knowledge' | 'prompt' | 'inputs' | 'tools' | 'outputs' | 'workflow' | 'api-settings' | 'publishing';
type DictationStatus = 'idle' | 'loading' | 'recording' | 'stopped' | 'error' | 'permission-denied';

const STEP_LABELS: Record<StepKey, string> = {
  general: 'General',
  knowledge: 'Knowledge',
  prompt: 'Prompt',
  inputs: 'Inputs',
  tools: 'Tools',
  outputs: 'Outputs',
  workflow: 'Workflow',
  'api-settings': 'API Settings',
  publishing: 'Publishing',
};

const stepsForAgentType = (type: AgentType): StepKey[] => {
  if (type === 'KNOWLEDGE') return ['general', 'knowledge', 'prompt', 'publishing'];
  if (type === 'WORKFLOW') return ['general', 'workflow', 'publishing'];
  if (type === 'API') return ['general', 'knowledge', 'prompt', 'inputs', 'tools', 'outputs', 'api-settings', 'publishing'];
  return ['general', 'knowledge', 'prompt', 'inputs', 'tools', 'outputs', 'publishing'];
};

// ── Icon Picker Modal ─────────────────────────────────────────────────────────

interface IconPickerModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (icon: string) => void;
}

const IconPickerModal: React.FC<IconPickerModalProps> = ({ open, onClose, onSelect }) => {
  const [search, setSearch] = useState('');
  const [pickerTab, setPickerTab] = useState<'icons' | 'upload'>('icons');

  const filtered = search
    ? PICKER_ICON_NAMES.filter((n) => n.toLowerCase().includes(search.toLowerCase()))
    : DEFAULT_PICKER_ICON_NAMES;

  const handleUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      if (result) {
        onSelect(result);
        onClose();
      }
    };
    reader.readAsDataURL(file);
    return false;
  };

  return (
    <Modal
      open={open}
      title={<span style={{ color: '#ececec' }}>Choose Icon</span>}
      onCancel={onClose}
      footer={null}
      width={640}
      className="praxys-agent-icon-picker-modal"
    >
      <Tabs
        activeKey={pickerTab}
        onChange={(k) => setPickerTab(k as any)}
        items={[
          {
            key: 'icons',
            label: 'Antd Icons',
            children: (
              <div>
                <Input
                  placeholder="Search icons…"
                  prefix={<SearchOutlined />}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  allowClear
                  style={{ marginBottom: 12, background: '#2a2a2a', borderColor: '#3f3f3f', color: '#ececec' }}
                />
                {!search && (
                  <Text style={{ color: '#6b7280', display: 'block', fontSize: 12, marginBottom: 10 }}>
                    Popular Ant Design icons
                  </Text>
                )}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(60px, 1fr))',
                    gap: 6,
                    maxHeight: 360,
                    overflowY: 'auto',
                    padding: '4px 2px',
                  }}
                >
				{filtered.map((name) => {
				  const IconComp = (AntdIcons as any)[name] as React.ComponentType<any>;
				  const displayName = name.replace('Outlined', '');

				  return (
				    <Tooltip title={displayName} key={name}>
				      <div
				        onClick={() => { onSelect(name); onClose(); }}
				        style={{
				          display: 'flex',
				          flexDirection: 'column',
				          alignItems: 'center',
				          justifyContent: 'center',
				          gap: 6,
				          minHeight: 76,
				          padding: '10px 6px',
				          borderRadius: 8,
				          cursor: 'pointer',
				          background: '#2a2a2a',
				          border: '1px solid #3f3f3f',
				          color: '#94a3b8',
				          transition: 'all 0.15s',
				        }}
				      >
				        <IconComp style={{ fontSize: 24 }} />
				        <span
				          style={{
				            fontSize: 10,
				            maxWidth: '100%',
				            overflow: 'hidden',
				            textOverflow: 'ellipsis',
				            whiteSpace: 'nowrap',
				          }}
				        >
				          {displayName}
				        </span>
				      </div>
				    </Tooltip>
				  );
				})}
                </div>
              </div>
            ),
          },
          {
            key: 'upload',
            label: 'Upload Image',
            children: (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '32px 0', gap: 16 }}>
                <PictureOutlined style={{ fontSize: 48, color: '#6366f1' }} />
                <Text style={{ color: '#94a3b8', textAlign: 'center' }}>
                  Upload a PNG, JPG, SVG, or WebP image to use as your agent's icon.
                  <br />
                  Recommended size: 64×64 px or larger.
                </Text>
                <Upload
                  accept="image/*"
                  showUploadList={false}
                  beforeUpload={handleUpload}
                >
                  <Button icon={<UploadOutlined />} style={{ background: '#2a2a2a', borderColor: '#3f3f3f', color: '#ececec' }}>
                    Choose Image
                  </Button>
                </Upload>
              </div>
            ),
          },
        ]}
      />
    </Modal>
  );
};

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Convert structured input fields to a JSON schema record */
const fieldsToSchema = (fields: InputField[]): Record<string, any> => {
  const schema: Record<string, any> = {};
  fields.forEach((f) => {
    const name = String(f?.name || '').trim();
    if (name) {
      schema[name] = {
        type: String(f?.type || 'String').toLowerCase(),
        description: String(f?.description || ''),
        required: Boolean(f?.required),
        secret: Boolean(f?.secret),
      };
    }
  });
  return schema;
};

const outputFieldsToSchema = (fields: OutputField[]): Record<string, any> => {
  const schema: Record<string, any> = {};
  fields.forEach((f) => {
    const name = String(f?.name || '').trim();
    if (name) {
      schema[name] = {
        type: String(f?.type || 'String').toLowerCase(),
        description: String(f?.description || ''),
      };
    }
  });
  return schema;
};

/** Parse an input_schema record back into structured fields for editing */
const normalizeFieldType = (type: any): string => {
  const raw = String(type || 'String').trim();
  const found = FIELD_TYPES.find((fieldType) => fieldType.toLowerCase() === raw.toLowerCase());
  return found || 'String';
};

const schemaToInputFields = (schema: Record<string, any>): InputField[] => {
  const source = schema && typeof schema === 'object' ? schema : {};
  const properties = source.properties && typeof source.properties === 'object'
    ? source.properties
    : source;
  const requiredNames = new Set(Array.isArray(source.required) ? source.required.map(String) : []);
  return Object.entries(properties)
    .filter(([name]) => !['properties', 'required', '$schema', 'type', 'title', 'description'].includes(name))
    .map(([name, val]) => {
      const isObject = val && typeof val === 'object' && !Array.isArray(val);
      const fieldSchema = isObject ? (val as Record<string, any>) : {};
      return {
        name,
        type: normalizeFieldType(isObject ? fieldSchema.type : 'String'),
        description: isObject ? String(fieldSchema.description ?? '') : String(val ?? ''),
        required: isObject && 'required' in fieldSchema ? Boolean(fieldSchema.required) : requiredNames.has(name),
        secret: isObject ? Boolean(fieldSchema.secret || fieldSchema.format === 'password') : false,
      };
    });
};

const schemaToOutputFields = (schema: Record<string, any>): OutputField[] =>
  Object.entries(schema).map(([name, val]) => ({
    name,
    type: typeof val === 'object' && val.type
      ? (val.type as string).charAt(0).toUpperCase() + (val.type as string).slice(1)
      : 'String',
    description: typeof val === 'object' ? (val.description ?? '') : String(val),
  }));

const normalizeToolRecord = (tool: any): ToolRecord | null => {
  if (typeof tool === 'string') {
    const name = tool.trim();
    return name ? { name, description: '' } : null;
  }
  if (!tool || typeof tool !== 'object') return null;
  const name = String(tool.name || tool.id || tool.tool_name || '').trim();
  if (!name) return null;
  return {
    ...tool,
    name,
    description: String(tool.description || tool.summary || ''),
  };
};

const normalizeToolId = (tool: any): string => {
  if (typeof tool === 'string') return tool.trim();
  if (!tool || typeof tool !== 'object') return '';
  return String(tool.name || tool.id || tool.tool_name || '').trim();
};

const normalizeToolIds = (tools: any[]): string[] => {
  const seen = new Set<string>();
  const ids: string[] = [];
  (Array.isArray(tools) ? tools : []).forEach((tool) => {
    const id = normalizeToolId(tool);
    if (id && !seen.has(id)) {
      seen.add(id);
      ids.push(id);
    }
  });
  return ids;
};

const extractJsonObject = (text: string): Record<string, any> => {
  const raw = String(text || '').trim();
  const fenced = raw.match(/```(?:json)?\s*([\s\S]*?)```/i);
  const candidate = fenced ? fenced[1].trim() : raw;
  try {
    return JSON.parse(candidate);
  } catch {
    const start = candidate.indexOf('{');
    const end = candidate.lastIndexOf('}');
    if (start >= 0 && end > start) {
      return JSON.parse(candidate.slice(start, end + 1));
    }
    throw new Error('AI response did not contain a JSON object');
  }
};

const draftArrayToInputFields = (items: any[]): InputField[] => (
  Array.isArray(items) ? items : []
).map((item) => ({
  name: String(item?.name || '').trim(),
  type: normalizeFieldType(item?.type || 'String'),
  description: String(item?.description || ''),
  required: item?.required !== false,
  secret: Boolean(item?.secret),
})).filter((field) => field.name);

const draftArrayToOutputFields = (items: any[]): OutputField[] => (
  Array.isArray(items) ? items : []
).map((item) => ({
  name: String(item?.name || '').trim(),
  type: String(item?.type || 'String').trim() || 'String',
  description: String(item?.description || ''),
})).filter((field) => field.name);

const toolInputManifest = (tool: ToolRecord): string => {
  const name = normalizeToolId(tool);
  const schema = (tool as any).input_schema && typeof (tool as any).input_schema === 'object'
    ? (tool as any).input_schema
    : {};
  const properties = schema.properties && typeof schema.properties === 'object'
    ? schema.properties
    : ((tool as any).inputs && typeof (tool as any).inputs === 'object' ? (tool as any).inputs : {});
  const required = new Set(Array.isArray(schema.required) ? schema.required.map(String) : []);
  const requiredFields: string[] = [];
  const optionalFields: string[] = [];
  Object.entries(properties || {}).forEach(([fieldName, spec]: [string, any]) => {
    const isRequired = required.has(fieldName) || Boolean(spec?.required);
    (isRequired ? requiredFields : optionalFields).push(fieldName);
  });
  const parts = [
    name,
    tool.description ? `description: ${String(tool.description).slice(0, 140)}` : '',
    requiredFields.length ? `required inputs: ${requiredFields.join(', ')}` : '',
    optionalFields.length ? `optional inputs: ${optionalFields.slice(0, 8).join(', ')}` : '',
  ].filter(Boolean);
  return parts.join(' | ');
};

const selectedToolManifest = (tools: ToolRecord[], preferredTerms: string): string => {
  const lower = preferredTerms.toLowerCase();
  const likely = tools.filter((tool) => {
    const haystack = `${normalizeToolId(tool)} ${tool.description || ''}`.toLowerCase();
    return (
      haystack.includes('ssh') ||
      haystack.includes('email') ||
      haystack.includes('smtp') ||
      haystack.includes('http') ||
      haystack.includes('web') ||
      lower.split(/\W+/).some((term) => term.length > 3 && haystack.includes(term))
    );
  });
  const source = likely.length ? likely : tools;
  return source.slice(0, 24).map(toolInputManifest).join('\n');
};

/** Extract {{variable}} names from a prompt string */
const extractPromptVars = (prompt: string): string[] => {
  const matches = Array.from(prompt.matchAll(/\{\{(\w+)\}\}/g));
  const seen = new Set<string>();
  const vars: string[] = [];
  for (const m of matches) {
    if (!seen.has(m[1])) { seen.add(m[1]); vars.push(m[1]); }
  }
  return vars;
};

const formatAgentPrompt = (prompt: string): string => {
  const text = (prompt || '').replace(/\r\n/g, '\n').replace(/\s+/g, ' ').trim();
  if (!text) return '';
  const protectedText = text.replace(/\{\{\s*([A-Za-z0-9_.-]+)\s*\}\}/g, (_, name) => `{{${name}}}`);
  const sectionTerms = [
    'Role', 'Objective', 'Required Inputs', 'Inputs', 'Tool Configuration', 'Workflow',
    'Output Requirements', 'Error Handling', 'SMTP configuration', 'Semantic Goal',
    'Analysis Instructions', 'Sources', 'Notes',
  ];
  const sectionPattern = new RegExp(`\\b(${sectionTerms.map((s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})\\s*:`, 'gi');
  let next = protectedText.replace(sectionPattern, '\n\n$1:\n');
  next = next.replace(/\s+-\s+/g, '\n- ');
  next = next.replace(/\s+\*\s+/g, '\n* ');
  next = next.replace(/\s+(\d+)\.\s+/g, '\n$1. ');
  next = next.replace(/([.!?])\s+(?=(Use|When|If|Return|Do not|Include|Extract|Crawl|Search|Send|Validate|Summarize|Summarise|Analyze|Analyse)\b)/g, '$1\n');
  next = next.replace(/\n{3,}/g, '\n\n');
  return next.split('\n').map((line) => line.trim()).join('\n').trim();
};

// ── Wizard ────────────────────────────────────────────────────────────────────

const AgentBuilderWizard: React.FC<Props> = ({
  visible, userId, existingAgent, onClose, onSaved,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [kbs, setKbs] = useState<KBSummary[]>([]);
  const [availableTools, setAvailableTools] = useState<ToolRecord[]>([]);
  const [profileConnectors, setProfileConnectors] = useState<api.ConnectorInstance[]>([]);
  const [connectorBindings, setConnectorBindings] = useState<Record<string, string>>({});
  const [toolSearch, setToolSearch] = useState('');
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [builderMode, setBuilderMode] = useState<'manual' | 'ai'>('manual');
  const [aiBuilderPrompt, setAiBuilderPrompt] = useState('');
  const [aiBuilderLoading, setAiBuilderLoading] = useState(false);
  const [aiDictationStatus, setAiDictationStatus] = useState<DictationStatus>('idle');
  const [aiDictationTranscript, setAiDictationTranscript] = useState('');
  const [aiDictationError, setAiDictationError] = useState<string | null>(null);
  const aiRecognitionRef = useRef<any>(null);
  const aiDictationTranscriptRef = useRef('');
  const aiDictationCommitRef = useRef(true);
  const aiDictationErrorRef = useRef(false);

  // ── Step 0: General ────────────────────────────────────────────────────────
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [shortDescription, setShortDescription] = useState('');
  const [icon, setIcon] = useState('RocketOutlined');
  const [category, setCategory] = useState<AgentCategory>('CUSTOM');
  const [agentType, setAgentType] = useState<AgentType>('TASK');

  // ── Step 1: Knowledge ─────────────────────────────────────────────────────
  const [selectedKbIds, setSelectedKbIds] = useState<string[]>([]);

  // ── Step 2: Prompt ────────────────────────────────────────────────────────
  const [systemPrompt, setSystemPrompt] = useState('');

  // ── Step 3: Inputs ────────────────────────────────────────────────────────
  const [inputFields, setInputFields] = useState<InputField[]>([]);

  // ── Step 4: Tools ─────────────────────────────────────────────────────────
  const [selectedToolIds, setSelectedToolIds] = useState<string[]>([]);

  // Workflow-only configuration
  const [playbookId, setPlaybookId] = useState('');

  // ── Step 5: Outputs ───────────────────────────────────────────────────────
  const [outputFields, setOutputFields] = useState<OutputField[]>([]);

  // ── Step 6: Publishing ────────────────────────────────────────────────────
  const [visibility, setVisibility] = useState<AgentVisibility>('PRIVATE');
  const [allowScheduling, setAllowScheduling] = useState(true);
  const [allowApiExecution, setAllowApiExecution] = useState(true);
  const [apiKeyRequired, setApiKeyRequired] = useState(false);

  const isEditing = !!existingAgent;
  const stepKeys = stepsForAgentType(agentType);
  const activeStep = stepKeys[currentStep] || 'general';

  useEffect(() => {
    if (existingAgent) {
      setName(existingAgent.name);
      setDescription(existingAgent.description);
      setShortDescription(existingAgent.short_description);
      setIcon(existingAgent.icon || 'RocketOutlined');
      setCategory(existingAgent.category);
      setAgentType(existingAgent.agent_type || 'TASK');
      setPlaybookId(existingAgent.playbook_id || '');
      setSelectedKbIds(existingAgent.knowledge_base_ids || []);
      setSystemPrompt(existingAgent.system_prompt || '');
      setInputFields(schemaToInputFields(existingAgent.input_schema || {}));
      setSelectedToolIds(normalizeToolIds(existingAgent.tool_ids || []));
      setConnectorBindings(existingAgent.connector_bindings || {});
      setOutputFields(schemaToOutputFields(existingAgent.output_schema || {}));
      setVisibility(existingAgent.visibility);
      setAllowScheduling(existingAgent.allow_scheduling);
      setAllowApiExecution(existingAgent.allow_api_execution);
      setApiKeyRequired(existingAgent.api_key_required);
    }
  }, [existingAgent]);

  useEffect(() => {
    if (currentStep >= stepKeys.length) {
      setCurrentStep(Math.max(0, stepKeys.length - 1));
    }
  }, [currentStep, stepKeys.length]);

  useEffect(() => {
    if (visible) return;
    aiDictationCommitRef.current = false;
    aiRecognitionRef.current?.stop?.();
    aiRecognitionRef.current = null;
    aiDictationTranscriptRef.current = '';
    aiDictationErrorRef.current = false;
    setAiDictationStatus('idle');
    setAiDictationTranscript('');
    setAiDictationError(null);
  }, [visible]);

  useEffect(() => () => {
    aiDictationCommitRef.current = false;
    aiRecognitionRef.current?.stop?.();
  }, []);

  const appendAiDictationText = (spokenText: string) => {
    const cleanText = spokenText.trim();
    if (!cleanText) return;
    setAiBuilderPrompt((prev) => (prev.trim() ? `${prev.trim()} ${cleanText}` : cleanText));
  };

  const finalizeAiDictation = (commitTranscript: boolean) => {
    aiDictationCommitRef.current = commitTranscript;
    if (aiRecognitionRef.current) {
      setAiDictationStatus('stopped');
      aiRecognitionRef.current.stop();
      return;
    }
    setAiDictationStatus('idle');
    setAiDictationTranscript('');
    setAiDictationError(null);
  };

  const handleAiDictationCancel = () => {
    aiDictationErrorRef.current = false;
    finalizeAiDictation(false);
  };

  const handleAiDictationToggle = () => {
    const recognizerWindow = window as Window & {
      SpeechRecognition?: new () => any;
      webkitSpeechRecognition?: new () => any;
    };
    const RecognitionCtor = recognizerWindow.SpeechRecognition || recognizerWindow.webkitSpeechRecognition;
    if (!RecognitionCtor) {
      message.warning('Dictation is not supported in this browser.');
      return;
    }
    if (aiDictationStatus === 'recording' || aiDictationStatus === 'loading') {
      finalizeAiDictation(true);
      return;
    }
    aiRecognitionRef.current?.stop?.();
    try {
      const recognition = new RecognitionCtor();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';
      aiDictationTranscriptRef.current = '';
      aiDictationErrorRef.current = false;
      aiDictationCommitRef.current = true;
      setAiDictationStatus('loading');
      setAiDictationTranscript('');
      setAiDictationError(null);
      recognition.onstart = () => {
        setAiDictationStatus('recording');
      };
      recognition.onresult = (event: any) => {
        const transcriptParts: string[] = [];
        for (let i = event.resultIndex || 0; i < event.results.length; i += 1) {
          const result = event.results[i];
          const segment = String(result?.[0]?.transcript || '').trim();
          if (!segment) continue;
          transcriptParts.push(segment);
          if (result.isFinal) {
            aiDictationTranscriptRef.current = `${aiDictationTranscriptRef.current} ${segment}`.trim();
          }
        }
        if (transcriptParts.length === 0) return;
        const preview = [aiDictationTranscriptRef.current, transcriptParts.join(' ')].filter(Boolean).join(' ').trim();
        setAiDictationTranscript(preview);
      };
      recognition.onerror = (event: any) => {
        aiDictationErrorRef.current = true;
        aiDictationCommitRef.current = false;
        const permissionDenied = event?.error === 'not-allowed' || event?.error === 'service-not-allowed';
        setAiDictationStatus(permissionDenied ? 'permission-denied' : 'error');
        setAiDictationError(
          permissionDenied
            ? 'To use dictation, allow microphone access for Praxys.ai and retry.'
            : event?.message || 'We could not continue dictation. Please try again.',
        );
      };
      recognition.onend = () => {
        if (aiRecognitionRef.current === recognition) aiRecognitionRef.current = null;
        const finalTranscript = aiDictationTranscriptRef.current.trim();
        if (aiDictationCommitRef.current && !aiDictationErrorRef.current && finalTranscript) {
          appendAiDictationText(finalTranscript);
        }
        if (!aiDictationErrorRef.current) {
          setAiDictationStatus('idle');
          setAiDictationTranscript('');
          setAiDictationError(null);
        }
        aiDictationTranscriptRef.current = '';
        aiDictationErrorRef.current = false;
      };
      aiRecognitionRef.current = recognition;
      recognition.start();
    } catch (err: any) {
      const permissionDenied = err?.name === 'NotAllowedError' || err?.name === 'SecurityError';
      setAiDictationStatus(permissionDenied ? 'permission-denied' : 'error');
      setAiDictationError(
        permissionDenied
          ? 'To use dictation, allow microphone access for Praxys.ai and retry.'
          : 'Unable to start dictation.',
      );
      if (!permissionDenied) {
        message.error('Unable to start dictation.');
      }
    }
  };

  useEffect(() => {
    if (!visible) return;
    api.listKnowledgeBases({ user_id: userId }).then((res: any) => {
      setKbs(res.data.knowledge_bases || []);
    }).catch(() => {});
    api.listTools().then((res: any) => {
      const tools = Array.isArray(res.data.tools) ? res.data.tools : [];
      setAvailableTools(tools.map(normalizeToolRecord).filter(Boolean) as ToolRecord[]);
    }).catch(() => {});
    api.listConnectorInstances(userId).then((res) => {
      setProfileConnectors((res.data.instances || []).filter((instance) => instance.status === 'connected'));
    }).catch(() => setProfileConnectors([]));
  }, [userId, visible]);

  const isSocialPublishingAgent = (agentType === 'TASK' || agentType === 'API')
    && normalizeToolIds(selectedToolIds).some((id) => id.toUpperCase() === 'SOCIAL_PUBLISH_POST');

  const handleSave = async () => {
    if (!validateAllSteps()) return;
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        agent_type: agentType,
        description,
        short_description: shortDescription,
        icon,
        category,
        visibility,
        playbook_id: agentType === 'WORKFLOW' ? playbookId.trim() : '',
        knowledge_base_ids: agentType === 'WORKFLOW' ? [] : selectedKbIds,
        tool_ids: agentType === 'TASK' || agentType === 'API' ? normalizeToolIds(selectedToolIds) : [],
        connector_bindings: agentType === 'TASK' || agentType === 'API' ? connectorBindings : {},
        system_prompt: systemPrompt,
        input_schema: agentType === 'TASK' || agentType === 'API' ? fieldsToSchema(
          Object.keys(connectorBindings).length
            ? inputFields.filter((field) => !/^(connector_instance_id|.*(_api_key|_access_token|_auth_token|_password|_secret|_credentials))$/i.test(field.name))
            : inputFields,
        ) : {},
        output_schema: agentType === 'TASK' || agentType === 'API' ? outputFieldsToSchema(outputFields) : {},
        allow_scheduling: agentType === 'KNOWLEDGE' ? false : isSocialPublishingAgent || allowScheduling,
        allow_api_execution: agentType === 'API' ? true : allowApiExecution,
        api_key_required: agentType === 'API' ? apiKeyRequired : false,
      } satisfies api.CreateAgentPayload;

      if (isEditing && existingAgent) {
        await api.updateAgent(existingAgent.id, payload);
        message.success('Agent updated');
      } else {
        await api.createAgent(payload);
        message.success('Agent created');
      }
      onSaved();
    } catch (err: any) {
      message.error(err?.response?.data?.detail || 'Failed to save agent');
    } finally {
      setSaving(false);
    }
  };

  /** Shared style for dark form labels */
  const labelStyle: React.CSSProperties = { color: '#94a3b8' };
  /** Shared dark input style */
  const inputStyle: React.CSSProperties = { background: '#2a2a2a', borderColor: '#3f3f3f', color: '#ececec' };

  // ── Prompt variable suggestion ────────────────────────────────────────────
  const promptVars = extractPromptVars(systemPrompt);
  const existingInputNames = new Set(inputFields.map((f) => String(f?.name || '')));
  const suggestedVars = promptVars.filter((v) => !existingInputNames.has(v));

  const addSuggestedVar = (varName: string) => {
    setInputFields((prev) => [
      ...prev,
      { name: varName, type: 'String', description: '', required: true, secret: false },
    ]);
  };

  // ── Filtered tools ────────────────────────────────────────────────────────
  const filteredTools = toolSearch.trim()
    ? availableTools.filter(
        (t) =>
          String(t.name || '').toLowerCase().includes(toolSearch.toLowerCase()) ||
          String(t.description || '').toLowerCase().includes(toolSearch.toLowerCase()),
      )
    : availableTools;

  const toggleTool = (toolName: string) => {
    const cleanName = normalizeToolId(toolName);
    if (!cleanName) return;
    setSelectedToolIds((prev) =>
      prev.includes(cleanName) ? prev.filter((id) => id !== cleanName) : [...prev, cleanName],
    );
  };

  const applyAgentDraft = (draft: Record<string, any>) => {
    const nextType = String(draft.agent_type || draft.type || 'TASK').toUpperCase() as AgentType;
    const safeType: AgentType = ['KNOWLEDGE', 'TASK', 'WORKFLOW', 'API'].includes(nextType) ? nextType : 'TASK';
    const draftTools = normalizeToolIds(draft.tool_ids || draft.tools || []);
    const availableByLower = new Map(availableTools.map((tool) => [normalizeToolId(tool).toLowerCase(), normalizeToolId(tool)]));
    const matchedTools = draftTools
      .map((id) => availableByLower.get(id.toLowerCase()) || id)
      .filter(Boolean);

    setAgentType(safeType);
    setName(String(draft.name || '').trim() || 'New Agent');
    setShortDescription(String(draft.short_description || draft.shortDescription || '').trim());
    setDescription(String(draft.description || '').trim());
    setIcon(String(draft.icon || 'RocketOutlined'));
    if (AGENT_CATEGORIES.some((option) => option.value === draft.category)) {
      setCategory(draft.category);
    }
    setSystemPrompt(String(draft.system_prompt || draft.instructions || draft.prompt || '').trim());
    setInputFields(draftArrayToInputFields(draft.inputs || draft.input_fields || []));
    setOutputFields(draftArrayToOutputFields(draft.outputs || draft.output_fields || []));
    setSelectedToolIds(normalizeToolIds(matchedTools));
    setAllowScheduling(draft.allow_scheduling !== false);
    setAllowApiExecution(Boolean(draft.allow_api_execution));
    setVisibility('PRIVATE');
    setCurrentStep(0);
  };

  const generateAgentDraft = async () => {
    const request = aiBuilderPrompt.trim();
    if (!request) {
      message.warning('Describe the agent you want to create');
      return;
    }
    setAiBuilderLoading(true);
    try {
      const toolNames = availableTools.map((tool) => normalizeToolId(tool)).filter(Boolean);
      const toolManifest = selectedToolManifest(availableTools, request);
      const res = await api.chatWithLLM([
        {
          role: 'user',
          content: [
            'Create a draft Praxys.ai agent template from this request.',
            'Return only valid JSON. Do not include Markdown fences or commentary.',
            'Use only tools from this available tool list when possible:',
            toolNames.join(', ') || 'No tools loaded',
            '',
            'Tool-aware manifest. Use this to understand required configuration/input fields for selected tools:',
            toolManifest || 'No tool manifests available',
            '',
            'Required JSON shape:',
            '{',
            '  "agent_type": "TASK",',
            '  "name": "short agent name",',
            '  "short_description": "one sentence",',
            '  "description": "full description",',
            '  "category": "DEVOPS|OPERATIONS|RESEARCH|CUSTOM|...",',
            '  "icon": "RocketOutlined",',
            '  "system_prompt": "complete reusable instructions with {{variables}}",',
            '  "inputs": [{"name":"ip","type":"String","description":"Server host","required":true,"secret":false}],',
            '  "tools": ["SSH_EXECUTE"],',
            '  "outputs": [{"name":"report","type":"HTML","description":"Email-ready report"}],',
            '  "allow_scheduling": true,',
            '  "allow_api_execution": false',
            '}',
            '',
            'Rules:',
            '- Include every variable referenced in system_prompt as an input.',
            '- Mark passwords, tokens, secrets, and API keys as secret true and type Password.',
            '- For Slack, X, LinkedIn, Facebook, and Teams tools, use connected profile accounts; do not add API key, token, password, or connector_instance_id inputs. The agent builder provides connected accounts separately.',
            '- For SSH/docker/server inspection requests, include SSH-style inputs and the SSH tool if available.',
            '- When selecting a tool, read its required inputs from the tool-aware manifest.',
            '- The system_prompt must include a Tool configuration section for selected tools that need configuration.',
            '- Tool configuration values must use {{variables}} that are also present in the inputs array.',
            '- The system_prompt must be detailed, operational, and self-contained; do not summarize the user request in one short sentence.',
            '- Write the system_prompt with clear sections: Role, Objective, Required Inputs, Tool Configuration, Workflow, Output Requirements, and Error Handling.',
            '- In Workflow, describe the full sequence the agent should follow, including when to call each selected tool and how to use the tool results.',
            '- For research, discovery, web search, lead search, or opportunity search requests, include search criteria, ranking/filtering guidance, evidence to collect, and how to summarize the findings.',
            '- For web scraping or crawling requests, instruct the agent to use WEB_SCRAPER with depth 4, max_pages 20, and render_javascript true to index pages before analysis; do not use a character-limit-first approach.',
            '- Use same_domain_only true for a single-site crawl, but set same_domain_only false when the user asks to follow external linked resources such as third-party menus, booking pages, deal pages, bid portals, or product catalogs.',
            '- If WEB_SCRAPER is selected, include semantic_goal with the user requested concept, leave selector optional, and explain that blank selector means crawl/index the site and send the page corpus to the LLM for NLP analysis.',
            '- For government opportunity searches, tell the agent to infer opportunity pages conceptually (contracts, grants, solicitations, bids, procurement notices, jobs, tenders, RFPs), not just search for the literal word "opportunity".',
            '- For semantic extraction requests such as "contracting business opportunities", "happy hour deals opportunities", "food menu", product, or pricing, pass that phrase as semantic_goal and tell the agent to infer semantically relevant pages from the crawled corpus, not just keyword-match the requested words.',
            '- Do not require JSON output unless the user explicitly asks for JSON or an API payload. Otherwise prefer a concise research brief with titles, URLs, dates/deadlines, fit notes, and next actions.',
            '- If the user asks for an action after research, such as sending an email, include both the research step and the final action step in the system_prompt.',
            '- Include user-facing delivery details in the system_prompt, such as recipient, subject, email body expectations, attachments, and whether to send a summary or full results.',
            '- For email requests using SEND_EMAIL, include SMTP configuration in the system_prompt exactly in this style:',
            '  SMTP configuration:',
            '  * smtp.url = `{{smtp_url}}`',
            '  * smtp.port = `{{smtp_port}}`',
            '  * smtp.username = `{{smtp_username}}`',
            '  * smtp.password = `{{smtp_password}}`',
            '  * smtp.from_addr = `{{from_addr}}`',
            '- For email requests, include recipient and SMTP inputs only when the user asks to send email.',
            '- For email requests, include recipient_email, email_subject, and email_body_summary_style inputs unless the user gave fixed values.',
            '- Keep the system_prompt reusable and explicit enough for the planner to execute without guessing missing workflow steps.',
            '',
            `User request: ${request}`,
          ].join('\n'),
        },
      ], {
        resetSession: true,
        systemPrompt: 'You are an expert Praxys.ai agent template designer. Return strict JSON only.',
      });
      const draft = extractJsonObject(String((res.data as any)?.reply || (res.data as any)?.content || ''));
      applyAgentDraft(draft);
      message.success('AI draft applied. Review and tweak before saving.');
    } catch (err: any) {
      message.error(err?.response?.data?.detail || err?.message || 'Failed to generate agent draft');
    } finally {
      setAiBuilderLoading(false);
    }
  };

  const validateStep = (step: StepKey): boolean => {
    if (step === 'general') {
      if (!name.trim()) {
        message.error('Agent name is required');
        return false;
      }
      if (!category) {
        message.error('Category is required');
        return false;
      }
      if (!agentType) {
        message.error('Agent type is required');
        return false;
      }
    }
    if (step === 'prompt' && agentType !== 'WORKFLOW' && !systemPrompt.trim()) {
      message.error('Agent instructions are required');
      return false;
    }
    if (step === 'inputs') {
      const incomplete = inputFields.some((field) => !String(field?.name || '').trim() || !String(field?.type || '').trim());
      if (incomplete) {
        message.error('Each input must include a name and type');
        return false;
      }
      const names = inputFields.map((field) => String(field?.name || '').trim()).filter(Boolean);
      if (new Set(names).size !== names.length) {
        message.error('Input names must be unique');
        return false;
      }
    }
    if (step === 'outputs') {
      const incomplete = outputFields.some((field) => !String(field?.name || '').trim() || !String(field?.type || '').trim());
      if (incomplete) {
        message.error('Each output must include a name and type');
        return false;
      }
    }
    if (step === 'workflow' && !playbookId.trim()) {
      message.error('Workflow agents require a Playbook ID');
      return false;
    }
    return true;
  };

  const validateAllSteps = (): boolean => {
    for (const step of stepKeys) {
      if (!validateStep(step)) {
        setCurrentStep(stepKeys.indexOf(step));
        return false;
      }
    }
    return true;
  };

  const handleNext = () => {
    if (!validateStep(activeStep)) return;
    setCurrentStep((s) => s + 1);
  };

  const renderStepContent = () => {
    switch (activeStep) {
      // ── Step 0: General ──────────────────────────────────────────────────
      case 'general':
        return (
          <Form layout="vertical">
            <Form.Item label={<span style={labelStyle}>Agent Name <span style={{ color: '#ef4444' }}>*</span></span>}>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Docker Inspector"
                maxLength={100}
                style={inputStyle}
              />
            </Form.Item>
            <Form.Item label={<span style={labelStyle}>Short Description</span>}>
              <Input
                value={shortDescription}
                onChange={(e) => setShortDescription(e.target.value)}
                placeholder="One-line summary for cards and listings"
                maxLength={140}
                style={inputStyle}
              />
            </Form.Item>
            <Form.Item label={<span style={labelStyle}>Description</span>}>
              <TextArea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                placeholder="Detailed description of what this agent does"
                style={{ ...inputStyle, resize: 'vertical' }}
              />
            </Form.Item>
            <Form.Item label={<span style={labelStyle}>Category</span>}>
              <Select
                value={category}
                onChange={setCategory}
                options={AGENT_CATEGORIES}
                style={{ width: '100%' }}
                dropdownStyle={{ background: '#2a2a2a', borderColor: '#3f3f3f' }}
              />
            </Form.Item>
            <Form.Item label={<span style={labelStyle}>Agent Type</span>}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
                {AGENT_TYPES.map((type) => {
                  const selected = agentType === type.value;
                  return (
                    <div
                      key={type.value}
                      onClick={() => {
                        setAgentType(type.value);
                        setCurrentStep(0);
                      }}
                      style={{
                        border: `1px solid ${selected ? '#6366f1' : '#3f3f3f'}`,
                        background: selected ? '#1e293b' : '#1e1e1e',
                        borderRadius: 8,
                        padding: 12,
                        cursor: 'pointer',
                        display: 'flex',
                        gap: 10,
                        alignItems: 'flex-start',
                      }}
                    >
                      <span style={{ color: selected ? '#818cf8' : '#94a3b8', fontSize: 18 }}>
                        {type.icon}
                      </span>
                      <div>
                        <Text strong style={{ color: '#ececec', display: 'block' }}>{type.label}</Text>
                        <Text style={{ color: '#6b7280', fontSize: 12 }}>{type.description}</Text>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Form.Item>
            <Form.Item
              label={
                <span style={labelStyle}>
                  Icon&nbsp;
                  <Tooltip title="Select an antd icon or upload a custom image">
                    <InfoCircleOutlined style={{ color: '#6b7280' }} />
                  </Tooltip>
                </span>
              }
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div
                  style={{
                    width: 56, height: 56, display: 'flex', alignItems: 'center',
                    justifyContent: 'center', background: '#1e1e1e', border: '1px solid #3f3f3f',
                    borderRadius: 10, flexShrink: 0, cursor: 'pointer',
                  }}
                  onClick={() => setShowIconPicker(true)}
                >
                  {renderAgentIcon(icon, 28)}
                </div>
                <Button
                  onClick={() => setShowIconPicker(true)}
                  style={{ background: '#2a2a2a', borderColor: '#3f3f3f', color: '#ececec' }}
                >
                  Choose Icon…
                </Button>
                {icon && (
                  <Text style={{ color: '#6b7280', fontSize: 12 }}>
                    {icon.startsWith('data:') ? 'Custom image' : icon.replace('Outlined', '')}
                  </Text>
                )}
              </div>
            </Form.Item>
          </Form>
        );

      // ── Step 1: Knowledge ────────────────────────────────────────────────
      case 'knowledge':
        return (
          <div>
            <Text style={{ color: '#94a3b8', display: 'block', marginBottom: 16 }}>
              Attach Knowledge Bases to inject relevant context automatically during execution.
              Knowledge is optional — many agents operate without a KB.
            </Text>
            <Form layout="vertical">
              <Form.Item label={<span style={labelStyle}>Knowledge Bases</span>}>
                <Select
                  mode="multiple"
                  value={selectedKbIds}
                  onChange={setSelectedKbIds}
                  placeholder="Select one or more knowledge bases…"
                  style={{ width: '100%' }}
                  dropdownStyle={{ background: '#2a2a2a', borderColor: '#3f3f3f' }}
                >
                  {kbs.map((kb) => (
                    <Select.Option key={kb.id} value={kb.id}>
                      <BookOutlined style={{ marginRight: 6, color: '#6366f1' }} />
                      {kb.name}
                      {kb.description && (
                        <Text style={{ color: '#6b7280', fontSize: 11, marginLeft: 8 }}>
                          — {kb.description}
                        </Text>
                      )}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Form>
            {selectedKbIds.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <Text style={{ color: '#6b7280', fontSize: 12 }}>Attached:</Text>
                <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {selectedKbIds.map((id) => {
                    const kb = kbs.find((k) => k.id === id);
                    return (
                      <Tag
                        key={id}
                        color="blue"
                        closable
                        onClose={() => setSelectedKbIds((prev) => prev.filter((x) => x !== id))}
                      >
                        <BookOutlined style={{ marginRight: 4 }} />
                        {kb?.name || id}
                      </Tag>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );

      // ── Step 2: Prompt ───────────────────────────────────────────────────
      case 'prompt':
        return (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', marginBottom: 8 }}>
              <Text style={{ color: '#94a3b8' }}>
                Write the instructions for your agent. Use <code style={{ color: '#818cf8' }}>{`{{variable}}`}</code> syntax to reference inputs.
              </Text>
              <Tooltip title="Format prompt into readable sections and lists">
                <Button
                  size="small"
                  icon={<MenuOutlined />}
                  onClick={() => {
                    const formatted = formatAgentPrompt(systemPrompt);
                    if (!formatted) {
                      message.warning('Add prompt text before formatting');
                      return;
                    }
                    setSystemPrompt(formatted);
                  }}
                >
                  Format
                </Button>
              </Tooltip>
            </div>
            <TextArea
              value={systemPrompt}
              onChange={(e) => setSystemPrompt(e.target.value)}
              rows={12}
              placeholder={`You are a Docker Infrastructure Inspector.\n\nUse the SSH tool to connect to {{ip}} as {{username}}.\n\nInspect all running containers and return:\n- Container Name\n- Image\n- Status\n- Exposed Ports\n\nReturn results as structured JSON.`}
              style={{ fontFamily: 'monospace', fontSize: 12, ...inputStyle, resize: 'vertical' }}
            />
            {suggestedVars.length > 0 && (
              <div
                style={{
                  marginTop: 12,
                  background: '#1e293b',
                  border: '1px solid #334155',
                  borderRadius: 8,
                  padding: 12,
                }}
              >
                <Text style={{ color: '#94a3b8', fontSize: 12 }}>
                  <InfoCircleOutlined style={{ marginRight: 6, color: '#6366f1' }} />
                  Variables detected in prompt — add them as Inputs:
                </Text>
                <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {suggestedVars.map((v) => (
                    <Tag
                      key={v}
                      color="purple"
                      style={{ cursor: 'pointer' }}
                      onClick={() => addSuggestedVar(v)}
                    >
                      <PlusOutlined style={{ marginRight: 4 }} />{`{{${v}}}`}
                    </Tag>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      // ── Step 3: Inputs ───────────────────────────────────────────────────
      case 'inputs':
        return (
          <div>
            <Text style={{ color: '#94a3b8', display: 'block', marginBottom: 12 }}>
              Define the inputs callers must provide when running this agent.
            </Text>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {inputFields.map((field, idx) => (
                <div
                  key={idx}
                  style={{
                    background: '#1e1e1e',
                    border: '1px solid #2a2a2a',
                    borderRadius: 8,
                    padding: '10px 12px',
                    display: 'flex',
                    gap: 8,
                    alignItems: 'center',
                    flexWrap: 'wrap',
                  }}
                >
                  <Input
                    value={field.name}
                    onChange={(e) => {
                      const updated = [...inputFields];
                      updated[idx] = { ...updated[idx], name: e.target.value };
                      setInputFields(updated);
                    }}
                    placeholder="name"
                    style={{ ...inputStyle, width: 120, fontFamily: 'monospace', fontSize: 12 }}
                  />
                  <Select
                    value={field.type}
                    onChange={(v) => {
                      const updated = [...inputFields];
                      updated[idx] = { ...updated[idx], type: v };
                      setInputFields(updated);
                    }}
                    style={{ width: 110 }}
                    dropdownStyle={{ background: '#2a2a2a' }}
                    options={FIELD_TYPES.map((t) => ({ value: t, label: t }))}
                  />
                  <Input
                    value={field.description}
                    onChange={(e) => {
                      const updated = [...inputFields];
                      updated[idx] = { ...updated[idx], description: e.target.value };
                      setInputFields(updated);
                    }}
                    placeholder="description"
                    style={{ ...inputStyle, flex: 1, minWidth: 120, fontSize: 12 }}
                  />
                  <Tooltip title="Required">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Switch
                        size="small"
                        checked={field.required}
                        onChange={(v) => {
                          const updated = [...inputFields];
                          updated[idx] = { ...updated[idx], required: v };
                          setInputFields(updated);
                        }}
                      />
                      <Text style={{ color: '#6b7280', fontSize: 11 }}>Req</Text>
                    </div>
                  </Tooltip>
                  <Tooltip title="Secret (masked in UI)">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Switch
                        size="small"
                        checked={field.secret}
                        onChange={(v) => {
                          const updated = [...inputFields];
                          updated[idx] = { ...updated[idx], secret: v };
                          setInputFields(updated);
                        }}
                      />
                      <Text style={{ color: '#6b7280', fontSize: 11 }}>Secret</Text>
                    </div>
                  </Tooltip>
                  <Button
                    size="small"
                    danger
                    type="text"
                    icon={<DeleteOutlined />}
                    onClick={() => setInputFields((prev) => prev.filter((_, i) => i !== idx))}
                  />
                </div>
              ))}
            </div>
            <Button
              icon={<PlusOutlined />}
              style={{ marginTop: 10, background: '#2a2a2a', borderColor: '#3f3f3f', color: '#ececec' }}
              onClick={() =>
                setInputFields((prev) => [
                  ...prev,
                  { name: '', type: 'String', description: '', required: true, secret: false },
                ])
              }
            >
              Add Input
            </Button>
            {inputFields.filter((f) => String(f?.name || '').trim()).length > 0 && (
              <div style={{ marginTop: 14 }}>
                <Text style={{ color: '#6b7280', fontSize: 12 }}>Generated Schema:</Text>
                <pre
                  style={{
                    background: '#0f172a',
                    border: '1px solid #1e293b',
                    borderRadius: 6,
                    padding: '8px 12px',
                    fontSize: 11,
                    color: '#818cf8',
                    marginTop: 6,
                    maxHeight: 120,
                    overflowY: 'auto',
                  }}
                >
                  {JSON.stringify(fieldsToSchema(inputFields), null, 2)}
                </pre>
              </div>
            )}
          </div>
        );

      // ── Step 4: Tools ────────────────────────────────────────────────────
      case 'tools':
        return (
          <div>
            <div style={{ marginBottom: 16 }}>
              <Text style={{ color: '#ececec', display: 'block', marginBottom: 8 }}>Profile connectors</Text>
              {(['slack', 'x', 'linkedin', 'facebook', 'teams'] as const).map((type) => {
                const accounts = profileConnectors.filter((instance) => instance.connector_type === type);
                return (
                  <div key={type} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                    <Text style={{ color: '#94a3b8', width: 85, textTransform: 'capitalize' }}>{type}</Text>
                    <Select
                      aria-label={`${type} profile connection`}
                      value={connectorBindings[type] || undefined}
                      placeholder={accounts.length ? 'No account selected' : 'Connect in Profile first'}
                      allowClear
                      disabled={!accounts.length}
                      style={{ flex: 1 }}
                      options={accounts.map((account) => ({ value: account.id, label: account.name }))}
                      onChange={(value) => setConnectorBindings((current) => {
                        const next = { ...current };
                        if (value) next[type] = value;
                        else delete next[type];
                        return next;
                      })}
                    />
                  </div>
                );
              })}
              <Text style={{ color: '#6b7280', fontSize: 12 }}>Shared agents use each runner's own connected account.</Text>
            </div>
            <Text style={{ color: '#94a3b8', display: 'block', marginBottom: 12 }}>
              Select the Praxys tools this agent can use during execution.
            </Text>
            <Input
              placeholder="Search tools…"
              prefix={<SearchOutlined style={{ color: '#6b7280' }} />}
              value={toolSearch}
              onChange={(e) => setToolSearch(e.target.value)}
              allowClear
              style={{ ...inputStyle, marginBottom: 12 }}
            />
            {availableTools.length === 0 ? (
              <Text style={{ color: '#6b7280', fontSize: 12 }}>
                Loading tools… (tool registry connection required)
              </Text>
            ) : (
              <div
                style={{
                  maxHeight: 280,
                  overflowY: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                }}
              >
                {filteredTools.map((tool) => {
                  const toolName = normalizeToolId(tool.name);
                  const checked = selectedToolIds.includes(toolName);
                  return (
                    <div
                      key={toolName}
                      onClick={() => toggleTool(toolName)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: '8px 12px',
                        borderRadius: 6,
                        cursor: 'pointer',
                        background: checked ? '#1e293b' : '#1e1e1e',
                        border: `1px solid ${checked ? '#6366f1' : '#2a2a2a'}`,
                        transition: 'all 0.15s',
                      }}
                    >
                      <Checkbox checked={checked} style={{ pointerEvents: 'none' }} />
                      <ToolOutlined style={{ color: checked ? '#6366f1' : '#6b7280' }} />
                      <div style={{ flex: 1 }}>
                        <Text style={{ color: '#ececec', fontSize: 13 }}>{toolName}</Text>
                        {tool.description && (
                          <Text style={{ color: '#6b7280', fontSize: 11, display: 'block' }}>
                            {tool.description}
                          </Text>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {selectedToolIds.length > 0 && (
              <div style={{ marginTop: 10 }}>
                <Text style={{ color: '#6b7280', fontSize: 12 }}>
                  Selected ({selectedToolIds.length}):
                </Text>
                <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {normalizeToolIds(selectedToolIds).map((id) => (
                    <Tag
                      key={id}
                      color="geekblue"
                      closable
                      onClose={() => setSelectedToolIds((prev) => prev.filter((x) => x !== id))}
                    >
                      <ToolOutlined style={{ marginRight: 4 }} />{id}
                    </Tag>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      // ── Step 5: Outputs ──────────────────────────────────────────────────
      case 'outputs':
        return (
          <div>
            <Text style={{ color: '#94a3b8', display: 'block', marginBottom: 12 }}>
              Define the expected outputs this agent produces. Used for API documentation, marketplace listings, and workflow chaining.
            </Text>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {outputFields.map((field, idx) => (
                <div
                  key={idx}
                  style={{
                    background: '#1e1e1e',
                    border: '1px solid #2a2a2a',
                    borderRadius: 8,
                    padding: '10px 12px',
                    display: 'flex',
                    gap: 8,
                    alignItems: 'center',
                  }}
                >
                  <Input
                    value={field.name}
                    onChange={(e) => {
                      const updated = [...outputFields];
                      updated[idx] = { ...updated[idx], name: e.target.value };
                      setOutputFields(updated);
                    }}
                    placeholder="name"
                    style={{ ...inputStyle, width: 140, fontFamily: 'monospace', fontSize: 12 }}
                  />
                  <Select
                    value={field.type}
                    onChange={(v) => {
                      const updated = [...outputFields];
                      updated[idx] = { ...updated[idx], type: v };
                      setOutputFields(updated);
                    }}
                    style={{ width: 110 }}
                    dropdownStyle={{ background: '#2a2a2a' }}
                    options={OUTPUT_TYPES.map((t) => ({ value: t, label: t }))}
                  />
                  <Input
                    value={field.description}
                    onChange={(e) => {
                      const updated = [...outputFields];
                      updated[idx] = { ...updated[idx], description: e.target.value };
                      setOutputFields(updated);
                    }}
                    placeholder="description"
                    style={{ ...inputStyle, flex: 1, fontSize: 12 }}
                  />
                  <Button
                    size="small"
                    danger
                    type="text"
                    icon={<DeleteOutlined />}
                    onClick={() => setOutputFields((prev) => prev.filter((_, i) => i !== idx))}
                  />
                </div>
              ))}
            </div>
            <Button
              icon={<PlusOutlined />}
              style={{ marginTop: 10, background: '#2a2a2a', borderColor: '#3f3f3f', color: '#ececec' }}
              onClick={() =>
                setOutputFields((prev) => [...prev, { name: '', type: 'String', description: '' }])
              }
            >
              Add Output
            </Button>
            {outputFields.filter((f) => String(f?.name || '').trim()).length > 0 && (
              <div style={{ marginTop: 14 }}>
                <Text style={{ color: '#6b7280', fontSize: 12 }}>Generated Schema:</Text>
                <pre
                  style={{
                    background: '#0f172a',
                    border: '1px solid #1e293b',
                    borderRadius: 6,
                    padding: '8px 12px',
                    fontSize: 11,
                    color: '#10b981',
                    marginTop: 6,
                    maxHeight: 120,
                    overflowY: 'auto',
                  }}
                >
                  {JSON.stringify(outputFieldsToSchema(outputFields), null, 2)}
                </pre>
              </div>
            )}
          </div>
        );

      // ── Step 6: Publishing ───────────────────────────────────────────────
      case 'workflow':
        return (
          <div>
            <Text style={{ color: '#94a3b8', display: 'block', marginBottom: 12 }}>
              Workflow agents are the only agents backed by a Playbook.
            </Text>
            <Form layout="vertical">
              <Form.Item label={<span style={labelStyle}>Playbook ID <span style={{ color: '#ef4444' }}>*</span></span>}>
                <Input
                  value={playbookId}
                  onChange={(e) => setPlaybookId(e.target.value)}
                  placeholder="Existing playbook name or ID"
                  style={inputStyle}
                />
              </Form.Item>
            </Form>
          </div>
        );

      case 'api-settings':
        return (
          <div>
            <Form layout="vertical">
              <Form.Item
                label={
                  <span style={labelStyle}>
                    Require API Key&nbsp;
                    <Tooltip title="Require callers to authenticate before running this API agent">
                      <ApiOutlined style={{ color: '#6b7280' }} />
                    </Tooltip>
                  </span>
                }
              >
                <Switch
                  checked={apiKeyRequired}
                  onChange={setApiKeyRequired}
                  checkedChildren="Required"
                  unCheckedChildren="Optional"
                />
              </Form.Item>
            </Form>
            <div
              style={{
                background: '#1e1e1e',
                border: '1px solid #334155',
                borderRadius: 8,
                padding: 16,
                marginTop: 8,
              }}
            >
              <Text strong style={{ color: '#e2e8f0' }}>
                <GlobalOutlined style={{ marginRight: 6 }} />
                API Endpoint
              </Text>
              <pre
                style={{ color: '#818cf8', fontSize: 12, margin: '8px 0 0', whiteSpace: 'pre-wrap' }}
              >
                {`POST /api/agents/{agentId}/execute\n${JSON.stringify(
                  Object.fromEntries(inputFields.filter((f) => f.name).map((f) => [f.name, f.type.toLowerCase()])),
                  null,
                  2,
                )}`}
              </pre>
            </div>
          </div>
        );

      case 'publishing':
        return (
          <div>
            <Form layout="vertical">
              <Form.Item label={<span style={labelStyle}>Visibility</span>}>
                <Select
                  value={visibility}
                  onChange={setVisibility}
                  dropdownStyle={{ background: '#2a2a2a', borderColor: '#3f3f3f' }}
                >
                  {VISIBILITY_OPTIONS.map((o) => (
                    <Select.Option key={o.value} value={o.value}>
                      <span style={{ color: '#ececec' }}>{o.label}</span>
                      <Text style={{ color: '#6b7280', fontSize: 11, marginLeft: 8 }}>
                        — {o.description}
                      </Text>
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item label={<span style={labelStyle}>Allow Scheduling</span>}>
                <Switch
                  checked={agentType !== 'KNOWLEDGE' && (isSocialPublishingAgent || allowScheduling)}
                  onChange={setAllowScheduling}
                  disabled={agentType === 'KNOWLEDGE' || isSocialPublishingAgent}
                  checkedChildren="Enabled"
                  unCheckedChildren="Disabled"
                />
              </Form.Item>
              <Form.Item
                label={
                  <span style={labelStyle}>
                    Allow API Execution&nbsp;
                    <Tooltip title="POST /api/agents/{id}/execute">
                      <ApiOutlined style={{ color: '#6b7280' }} />
                    </Tooltip>
                  </span>
                }
              >
                <Switch
                  checked={agentType === 'API' || (agentType !== 'KNOWLEDGE' && allowApiExecution)}
                  onChange={setAllowApiExecution}
                  disabled={agentType === 'API' || agentType === 'KNOWLEDGE'}
                  checkedChildren="Enabled"
                  unCheckedChildren="Disabled"
                />
              </Form.Item>
            </Form>

            {agentType !== 'KNOWLEDGE' && agentType !== 'API' && (
              <div
                style={{
                  background: '#1e1e1e',
                  border: '1px solid #334155',
                  borderRadius: 8,
                  padding: 16,
                  marginTop: 8,
                }}
              >
                <Text strong style={{ color: '#e2e8f0' }}>
                  <GlobalOutlined style={{ marginRight: 6 }} />
                  Execution Endpoint
                </Text>
                <pre
                  style={{ color: '#818cf8', fontSize: 12, margin: '8px 0 0', whiteSpace: 'pre-wrap' }}
                >
                  POST /api/agents/{`{agentId}`}/execute
                </pre>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  const footer = (
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <Button
        onClick={currentStep === 0 ? onClose : () => setCurrentStep((s) => s - 1)}
        style={{ background: '#2a2a2a', borderColor: '#3f3f3f', color: '#ececec' }}
      >
        {currentStep === 0 ? 'Cancel' : 'Back'}
      </Button>
      <Space>
        {currentStep < stepKeys.length - 1 ? (
          <Button type="primary" onClick={handleNext}>
            Next
          </Button>
        ) : (
          <Button
            type="primary"
            icon={<CheckCircleOutlined />}
            loading={saving}
            onClick={handleSave}
          >
            {isEditing ? 'Save Changes' : 'Create Agent'}
          </Button>
        )}
      </Space>
    </div>
  );

  return (
    <>
      <Modal
        open={visible}
        title={
          <Title level={4} style={{ margin: 0, color: '#ececec', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 20, lineHeight: 1.2 }}>
              {isEditing ? <EditOutlined /> : <RocketOutlined />}
              {isEditing ? 'Edit Agent' : 'Create Agent'}
            </span>
          </Title>
        }
        onCancel={onClose}
        width={720}
        footer={footer}
        destroyOnClose
        className="praxys-agent-builder-modal"
      >
        {!isEditing && (
          <div
            style={{
              background: '#111827',
              border: '1px solid #243044',
              borderRadius: 8,
              padding: 12,
              marginBottom: 18,
            }}
          >
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: builderMode === 'ai' ? 12 : 0 }}>
              <Button
                size="small"
                className="praxys-agent-pill-btn"
                type={builderMode === 'manual' ? 'primary' : 'default'}
                onClick={() => setBuilderMode('manual')}
              >
                Manual
              </Button>
              <Button
                size="small"
                className="praxys-agent-pill-btn praxys-agent-pill-btn--accent"
                type={builderMode === 'ai' ? 'primary' : 'default'}
                icon={<RocketOutlined />}
                onClick={() => setBuilderMode('ai')}
              >
                AI Agent Builder
              </Button>
            </div>
            {builderMode === 'ai' && (
              <div>
                <Text style={{ color: '#94a3b8', display: 'block', marginBottom: 8 }}>
                  Describe the agent you want. The builder will fill the wizard with a draft you can review and tweak.
                </Text>
                <div style={{ position: 'relative', marginBottom: 10 }}>
                  <TextArea
                    rows={3}
                    value={aiBuilderPrompt}
                    onChange={(e) => setAiBuilderPrompt(e.target.value)}
                    placeholder="I would like to SSH to a server and check the stats for all Docker containers..."
                    style={{ ...inputStyle, paddingRight: 44 }}
                  />
                  <button
                    type="button"
                    className={`praxys-dictation-btn${aiDictationStatus === 'recording' || aiDictationStatus === 'loading' ? ' active' : ''}`}
                    onClick={handleAiDictationToggle}
                    disabled={aiBuilderLoading}
                    title={aiDictationStatus === 'recording' || aiDictationStatus === 'loading' ? 'Stop dictation' : 'Dictate agent description'}
                    aria-label={aiDictationStatus === 'recording' || aiDictationStatus === 'loading' ? 'Stop dictation' : 'Dictate agent description'}
                    style={{ position: 'absolute', right: 8, bottom: 8 }}
                  >
                    {aiDictationStatus === 'loading'
                      ? <LoadingOutlined style={{ fontSize: 14 }} />
                      : <AudioOutlined style={{ fontSize: 14 }} />}
                  </button>
                </div>
                <Button
                  type="primary"
                  icon={<RocketOutlined />}
                  loading={aiBuilderLoading}
                  onClick={generateAgentDraft}
                >
                  Generate Draft
                </Button>
              </div>
            )}
          </div>
        )}
        <Steps
          current={currentStep}
          size="small"
          style={{ marginBottom: 24 }}
          items={stepKeys.map((step) => ({
            title: <span style={{ color: '#94a3b8', fontSize: 11 }}>{STEP_LABELS[step]}</span>,
          }))}
        />
        <div style={{ minHeight: 340 }}>
          {renderStepContent()}
        </div>
      </Modal>

      <IconPickerModal
        open={showIconPicker}
        onClose={() => setShowIconPicker(false)}
        onSelect={(selected) => setIcon(selected)}
      />
      <DictationOverlay
        open={aiDictationStatus !== 'idle'}
        recording={aiDictationStatus === 'recording'}
        transcript={aiDictationTranscript}
        error={aiDictationStatus === 'error' || aiDictationStatus === 'permission-denied' ? aiDictationError : null}
        permissionDenied={aiDictationStatus === 'permission-denied'}
        onStop={() => finalizeAiDictation(true)}
        onCancel={handleAiDictationCancel}
      />
    </>
  );
};

export default AgentBuilderWizard;
